/**
 * K-MRS 약사 접근코드 발급 창.
 *
 * @spec:2026-10-15-KMRS-접근코드-PhAI-Admin-관리-전체기관 — 화면 설계 A, 사고 재구성 ①2·3
 *
 * 열람 범위를 셋 중 하나로 고른다: 기존 기관(프리셋) / ＋ 새 기관 직접 입력 / 전체 기관.
 * 발급 본문에는 고른 범위의 필드만 싣는다(범위를 바꿔도 다른 칸의 입력값은 화면에 남지만 전송하지 않는다).
 * 새 기관이면 [발급] → 확인 단계(만들고 발급)를 한 번 거친다. 「전체 기관」이면 경고가 고정된다.
 * 요청 중에는 [발급]·[취소]가 비활성이고, 연타해도 호출은 1회다.
 *
 * 창(이 컴포넌트)이 기관 목록 훅을 직접 구독한다 — 싱글턴 창이 연 시점 props로 고정되기 때문이다
 * (화면 설계 A 7, ARCHITECTURE §2의 명시적 예외).
 */
import React, { useRef, useState } from "react";
import Button from "components/atoms/Button";
import type { IssueAccessCodeBody } from "api/kmrsAdminService";
import { useKmrsOrganizations } from "hooks/kmrsAdmin/useKmrsAdmin";
import {
  CheckLabel,
  ErrorMsg,
  Field,
  FieldLabel,
  Hint,
  ModalBody,
  ModalFooter,
  ModalTitle,
  Notice,
  Select,
  TextInput,
} from "../style";
import {
  ALL_SCOPE_WARNING,
  validateCodeLabel,
  validateNewOrgId,
  validateNewOrgName,
} from "./kmrsText";

type Scope = "org-preset" | "org-new" | "all";

interface IssueCodeModalProps {
  isRel: boolean;
  /** 발급 — 성공하면 원문 창을 연다(호출부). 실패하면 던진다(사유는 훅이 토스트로 보여준다) */
  onIssue: (body: IssueAccessCodeBody) => Promise<void>;
  onCancel: () => void;
}

const IssueCodeModal: React.FC<IssueCodeModalProps> = ({ isRel, onIssue, onCancel }) => {
  const orgs = useKmrsOrganizations(true);
  const orgItems = orgs.data?.items ?? [];

  const [scope, setScope] = useState<Scope>("org-preset");
  const [organizationId, setOrganizationId] = useState("");
  const [newOrgId, setNewOrgId] = useState("");
  const [newOrgName, setNewOrgName] = useState("");
  const [label, setLabel] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef(false);

  const labelCheck = validateCodeLabel(label);
  const idCheck = validateNewOrgId(newOrgId);
  const nameCheck = validateNewOrgName(newOrgName);

  const scopeValid =
    scope === "all"
      ? true
      : scope === "org-preset"
        ? organizationId !== ""
        : idCheck.ok && nameCheck.ok;
  const canSubmit = labelCheck.ok && scopeValid && !submitting;

  // 고른 범위의 필드만 싣는다(화면 설계 A 7).
  const buildBody = (): IssueAccessCodeBody => {
    const trimmedLabel = label.trim();
    if (scope === "all") return { scope: "all", label: trimmedLabel };
    if (scope === "org-preset") return { scope: "org", organizationId, label: trimmedLabel };
    const name = newOrgName.trim();
    const newOrganization = name ? { id: newOrgId.trim(), name } : { id: newOrgId.trim() };
    return { scope: "org", newOrganization, label: trimmedLabel };
  };

  const doIssue = async (body: IssueAccessCodeBody) => {
    if (inFlight.current) return; // 연타 방지(렌더 타이밍과 무관하게 1회)
    inFlight.current = true;
    setSubmitting(true);
    try {
      await onIssue(body);
      // 성공하면 원문 창이 이 창을 대체한다(호출부). 여기서 닫지 않는다.
    } catch {
      // 새 기관이면 폼으로 돌아가 다시 고르게 한다(409면 프리셋이 새로 채워져 있다).
      setConfirming(false);
    } finally {
      setSubmitting(false);
      inFlight.current = false;
    }
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    if (scope === "org-new") {
      setConfirming(true); // 새 기관은 확인 단계를 한 번 거친다
      return;
    }
    void doIssue(buildBody());
  };

  const newOrgDisplay = newOrgName.trim() || newOrgId.trim();

  if (confirming) {
    return (
      <ModalBody data-testid="kmrs-issue-new-org-confirm">
        <ModalTitle>새 기관 만들기</ModalTitle>
        <div>
          {`새 기관 '${newOrgId.trim()}'(${newOrgDisplay})를 만들고 코드를 발급합니다. 기관은 나중에 지울 수 없어요.`}
        </div>
        <ModalFooter>
          <Button
            type="Text"
            size="Medium"
            variant="Ghost"
            color="Secondary"
            onClick={() => setConfirming(false)}
            disabled={submitting}
            data-testid="kmrs-issue-new-org-cancel"
          >
            취소
          </Button>
          <Button
            type="Text"
            size="Medium"
            variant="Contained"
            color="Primary"
            onClick={() => void doIssue(buildBody())}
            disabled={submitting}
            data-testid="kmrs-issue-new-org-confirm-ok"
          >
            만들고 발급
          </Button>
        </ModalFooter>
      </ModalBody>
    );
  }

  return (
    <ModalBody data-testid="kmrs-issue-form">
      <ModalTitle>접근코드 발급</ModalTitle>
      {isRel && <Notice $tone="warn">운영 환경입니다</Notice>}

      <Field as="div">
        <FieldLabel>열람 범위</FieldLabel>
        <CheckLabel>
          <input
            type="radio"
            name="kmrs-scope"
            checked={scope === "org-preset"}
            onChange={() => setScope("org-preset")}
            data-testid="kmrs-issue-scope-preset"
          />
          기존 기관 선택
        </CheckLabel>
        <CheckLabel>
          <input
            type="radio"
            name="kmrs-scope"
            checked={scope === "org-new"}
            onChange={() => setScope("org-new")}
            data-testid="kmrs-issue-scope-new"
          />
          ＋ 새 기관 직접 입력
        </CheckLabel>
        <CheckLabel>
          <input
            type="radio"
            name="kmrs-scope"
            checked={scope === "all"}
            onChange={() => setScope("all")}
            data-testid="kmrs-issue-scope-all"
          />
          전체 기관
        </CheckLabel>
      </Field>

      {scope === "org-preset" &&
        (orgs.isLoading ? (
          <Hint>불러오는 중입니다.</Hint>
        ) : orgs.isError ? (
          <Notice $tone="warn" data-testid="kmrs-issue-org-error">
            기관 목록을 불러오지 못했어요{" "}
            <Button type="Text" size="XSmall" variant="Ghost" color="Secondary" onClick={() => orgs.refetch()} data-testid="kmrs-issue-org-retry">
              다시
            </Button>
          </Notice>
        ) : orgItems.length === 0 ? (
          <Hint data-testid="kmrs-issue-org-empty">등록된 기관이 없어요</Hint>
        ) : (
          <Field>
            <FieldLabel>기관</FieldLabel>
            <Select
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
              data-testid="kmrs-issue-org-select"
            >
              <option value="">기관 선택</option>
              {orgItems.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </Select>
          </Field>
        ))}

      {scope === "org-new" && (
        <>
          <Field>
            <FieldLabel>새 기관 ID</FieldLabel>
            <TextInput
              value={newOrgId}
              onChange={(e) => setNewOrgId(e.target.value)}
              placeholder="예: incheon-pha"
              data-testid="kmrs-issue-new-org-id"
            />
            <Hint>영문 소문자·숫자·하이픈 1~32자</Hint>
            {newOrgId.trim() !== "" && !idCheck.ok && (
              <ErrorMsg data-testid="kmrs-issue-new-org-id-error">{idCheck.message}</ErrorMsg>
            )}
          </Field>
          <Field>
            <FieldLabel>표시 이름</FieldLabel>
            <TextInput
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value)}
              placeholder="예: 인천시약사회"
              data-testid="kmrs-issue-new-org-name"
            />
            <Hint>비우면 ID를 그대로 씁니다 (최대 40자)</Hint>
            {!nameCheck.ok && <ErrorMsg data-testid="kmrs-issue-new-org-name-error">{nameCheck.message}</ErrorMsg>}
          </Field>
        </>
      )}

      {scope === "all" && (
        <Notice $tone="warn" data-testid="kmrs-issue-all-warning">
          {ALL_SCOPE_WARNING}
        </Notice>
      )}

      <Field>
        <FieldLabel>이름표</FieldLabel>
        <TextInput
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="예: 부스1 김약사"
          data-testid="kmrs-issue-label"
        />
        {label.trim() !== "" && !labelCheck.ok && (
          <ErrorMsg data-testid="kmrs-issue-label-error">{labelCheck.message}</ErrorMsg>
        )}
      </Field>

      <ModalFooter>
        <Button
          type="Text"
          size="Medium"
          variant="Ghost"
          color="Secondary"
          onClick={onCancel}
          disabled={submitting}
          data-testid="kmrs-issue-cancel"
        >
          취소
        </Button>
        <Button
          type="Text"
          size="Medium"
          variant="Contained"
          color="Primary"
          onClick={handleSubmit}
          disabled={!canSubmit}
          data-testid="kmrs-issue-submit"
        >
          발급
        </Button>
      </ModalFooter>
    </ModalBody>
  );
};

export default IssueCodeModal;
